import DOMPurify from 'dompurify';
import _debounce from 'lodash/debounce';
import Quill from 'quill';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import 'quill-mention/autoregister';
import 'quill/dist/quill.snow.css';
import './styles.css';
import EmojiPickerButton from '@/ui/components/EmojiPickerButton';
import formatPersonName from '@/utils/formatPersonName';

import { Props } from './types';

const buildToolbar = (showUnderline: boolean, showHeaders: boolean) => {
  const formatting = ['bold', 'italic', ...(showUnderline ? ['underline'] : [])];
  const groups: (string | Record<string, unknown>)[][] = [formatting];
  if (showHeaders) groups.push([{ header: 1 }, { header: 2 }]);
  groups.push([{ list: 'ordered' }, { list: 'bullet' }]);
  return groups;
};

const DEFAULT_EMPTY_VALUE = '<p><br/></p>';

interface IMention {
  id: string;
  profile_image_url?: string;
  value: string;
}

/**
 * @deprecated Do not use this component. There is no equivalent yet at the root
 * of the UI folder — a new version should be built there using the current
 * design-system patterns (tailwind-variants + Headless UI, see src/ui/Button)
 * instead of using this legacy component.
 */
const RichTextEditor = ({
  disabled,
  fetchMentions,
  onChange,
  onImagePaste,
  placeholder,
  showEmojis,
  showHeaders = true,
  showUnderline = true,
  toolbar,
  value
}: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstance = useRef<Quill | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const lastContent = useRef<string | null>(null);
  const [emojiButtonContainer, setEmojiButtonContainer] = useState<HTMLElement | null>(null);

  const setContent = (value: string) => {
    if (!quillInstance.current) return;

    // Using .clipboard.dangerouslyPasteHTML() tells Quill to properly
    // interpret the HTML and insert the content through its delta engine,
    // which supports complex elements like <ul>, <ol>, <li>, <h4>, etc.
    // Directly modifying innerHTML bypasses that and results in stripped-out
    // or misinterpreted markup.
    const safeHTML = DOMPurify.sanitize(value || DEFAULT_EMPTY_VALUE);
    quillInstance.current.clipboard.dangerouslyPasteHTML(safeHTML);
    lastContent.current = value;
  };

  useEffect(() => {
    if (
      value !== undefined &&
      value !== lastContent.current &&
      value !== quillInstance.current?.root.innerHTML
    ) {
      setContent(value);
    }
  }, [value]);

  const fetchPeople = useCallback(
    async (search: string) => {
      if (!search || !fetchMentions) return [];

      controllerRef.current?.abort();
      controllerRef.current = new AbortController();

      try {
        const results = await fetchMentions({ page: '1', search });
        return results.data?.results || [];
      } catch {
        return [];
      }
    },
    [fetchMentions]
  );

  const debouncedFetchPeople = useMemo(
    () =>
      _debounce((searchTerm: string, renderList: (matches: IMention[]) => void) => {
        if (!fetchMentions) return;

        renderList([{ id: 'loading', value: 'Loading...' }]);

        fetchPeople(searchTerm).then((matches) => {
          renderList(
            matches.map((person) => ({
              id: person.id,
              profile_image_url: person.profile_image_url,
              value: formatPersonName({ person, withNickname: true })
            }))
          );
        });
      }, 200),
    [fetchPeople, fetchMentions]
  );

  const suggestPeople = useCallback(
    (searchTerm: string, renderList: (matches: IMention[]) => void) => {
      if (fetchMentions) {
        debouncedFetchPeople(searchTerm, renderList);
      }
    },
    [debouncedFetchPeople, fetchMentions]
  );

  useEffect(() => {
    if (editorRef.current && !quillInstance.current) {
      const modules: Record<string, unknown> = {
        toolbar: toolbar ?? buildToolbar(showUnderline, showHeaders)
      };

      if (fetchMentions) {
        modules.mention = {
          allowedChars: /^[A-Za-z\s]*$/,
          mentionDenotationChars: ['@'],
          source: (searchTerm: string, renderList: (matches: IMention[]) => void) => {
            if (!searchTerm) return;
            return suggestPeople(searchTerm, renderList);
          },
          renderItem: (item: IMention) => {
            const container = document.createElement('div');
            container.classList.add('mention-item');

            if (item.id === 'loading') {
              container.textContent = 'Loading...';
              container.classList.add('mention-loading');
            } else {
              const img = document.createElement('img');
              img.src = item.profile_image_url || '';
              img.classList.add('mention-avatar');

              const text = document.createElement('span');
              text.classList.add('mention-text');
              text.textContent = item.value;

              container.appendChild(img);
              container.appendChild(text);
            }

            return container;
          }
        };
      }

      quillInstance.current = new Quill(editorRef.current, {
        theme: 'snow',
        modules,
        placeholder,
        readOnly: disabled
      });

      if (showEmojis && !disabled) {
        // Quill inserts .ql-toolbar as a sibling BEFORE editorRef in the parent — not inside it
        const qlToolbar = editorRef.current.parentElement?.querySelector('.ql-toolbar');
        if (qlToolbar) {
          const span = document.createElement('span');
          span.className = 'ql-formats';
          qlToolbar.appendChild(span);
          setEmojiButtonContainer(span);
        }
      }

      quillInstance.current.on('text-change', () => {
        const html = quillInstance.current?.getSemanticHTML() || '';
        lastContent.current = html;
        onChange?.(html);
      });

      setContent(value || DEFAULT_EMPTY_VALUE);
    }
  }, [
    onChange,
    suggestPeople,
    fetchMentions,
    disabled,
    placeholder,
    toolbar,
    value,
    showEmojis,
    showHeaders,
    showUnderline
  ]);

  useEffect(() => {
    const editorEl = editorRef.current;
    if (!editorEl || !onImagePaste) return;

    const handlePaste = (e: ClipboardEvent) => {
      const imageItem = Array.from(e.clipboardData?.items ?? []).find((item) =>
        item.type.startsWith('image/')
      );
      const file = imageItem?.getAsFile();
      if (!file) return;
      e.preventDefault();
      e.stopPropagation();
      onImagePaste(file);
    };

    const handleDrop = (e: DragEvent) => {
      const file = Array.from(e.dataTransfer?.files ?? []).find((f) => f.type.startsWith('image/'));
      if (!file) return;
      e.preventDefault();
      e.stopPropagation();
      onImagePaste(file);
    };

    // Capture phase + stopPropagation: Quill's own paste/drop handlers run on
    // bubble and would insert their base64 embed before a bubble-phase
    // listener could stop them.
    editorEl.addEventListener('paste', handlePaste, { capture: true });
    editorEl.addEventListener('drop', handleDrop, { capture: true });
    return () => {
      editorEl.removeEventListener('paste', handlePaste, { capture: true });
      editorEl.removeEventListener('drop', handleDrop, { capture: true });
    };
  }, [onImagePaste]);

  const handleEmojiClick = useCallback((emoji: string) => {
    if (!quillInstance.current) return;
    // getSelection(true) refocuses the editor before reading the cursor position
    const range = quillInstance.current.getSelection(true);
    const insertAt = range?.index ?? Math.max(0, quillInstance.current.getLength() - 1);
    quillInstance.current.insertText(insertAt, emoji);
    quillInstance.current.setSelection(insertAt + emoji.length, 0);
  }, []);

  return (
    <div className="rounded-lg border border-gray-300 overflow-hidden">
      <div className={disabled ? 'ql-disabled' : ''} ref={editorRef} />
      {emojiButtonContainer &&
        createPortal(
          <EmojiPickerButton className="ql-emoji-btn" onEmojiClick={handleEmojiClick} />,
          emojiButtonContainer
        )}
    </div>
  );
};

export default RichTextEditor;
